import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaShieldAlt,
  FaLock,
  FaEye,
  FaServer,
  FaWifi,
  FaUserShield,
  FaBug,
  FaKey,
  FaDatabase,
  FaCloud,
  FaUsers,
  FaChartLine,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaPhone,
  FaEnvelope,
  FaRocket,
  FaGraduationCap,
  FaStar,
  FaArrowRight,
  FaDownload,
  FaPlay,
  FaStop,
  FaSearch,
  FaBullseye,
  FaCog,
  FaLaptop,
  FaMobile,
  FaBuilding,
  FaCertificate,
} from "react-icons/fa";
import "./SecurityPage.css";

const SecurityPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeService, setActiveService] = useState("analysis");
  const [selectedThreat, setSelectedThreat] = useState(null);

  const securityServices = [
    {
      id: "analysis",
      icon: FaSearch,
      title: "Analýza zabezpečenia",
      price: "Na základe rozsahu",
      description:
        "Analýza súčasného stavu Vašich počítačov z hľadiska zabezpečenia",
      features: [
        "Identifikácia slabých miest systému",
        "Protokol o stave zabezpečenia",
        "Návrh riešenia problémov",
        "Odporúčania na zlepšenie",
        "Analýza firemnej siete",
        "Kontrola aktualizácií systému",
      ],
      duration: "2-5 pracovných dní",
      deliverables:
        "Bezpečnostný protokol + Návrh riešenia + Implementačný plán",
    },
    {
      id: "maintenance",
      icon: FaCog,
      title: "Pravidelná údržba",
      price: "Od €50/mesiac",
      description:
        "Pravidelné mesačné kontroly Vašich počítačov a počítačovej siete",
      features: [
        "Mesačné preventívne kontroly",
        "Aktualizácie zabezpečenia",
        "Kontrola antivírusového softvéru",
        "Monitoring firemnej siete",
        "Predchádzanie problémom",
        "Servisná zmluva so zľavou 20%",
      ],
      duration: "Kontinuálna služba",
      deliverables: "Mesačné reporty + Preventívne opatrenia + 24/7 podpora",
    },
    {
      id: "comprehensive",
      icon: FaShieldAlt,
      title: "Komplexné riešenie",
      price: "Individuálna ponuka",
      description:
        "Návrh a realizácia komplexného riešenia bezpečnosti počítačov a siete",
      features: [
        "Vypracovanie bezpečnostného projektu",
        "Implementácia bezpečnostných opatrení",
        "Konfigurácia firewallov a antivírusov",
        "Návrh bezpečnostných politík",
        "Školenie zamestnancov",
        "Dokumentácia a postupy",
      ],
      duration: "1-4 týždne",
      deliverables:
        "Bezpečnostný projekt + Implementácia + Dokumentácia + Školenia",
    },
    {
      id: "software",
      icon: FaKey,
      title: "Softvérové riešenia",
      price: "Podľa licencií",
      description: "Predaj a legalizácia softvérového vybavenia a konzultácie",
      features: [
        "Legalizácia Microsoft® Windows®",
        "Legalizácia Microsoft® Office®",
        "Kontrola legálnosti softvéru",
        "Antivírový softvér",
        "Bezpečnostné nástroje",
        "Konzultačné služby",
      ],
      duration: "1-3 pracovné dni",
      deliverables: "Licencie + Inštalácia + Konfigurácia + Dokumentácia",
    },
    {
      id: "maintenance_physical",
      icon: FaServer,
      title: "Fyzická údržba",
      price: "Od €30/počítač",
      description:
        "Profylaktika počítačov - fyzické prečistenie a kontrola stavu",
      features: [
        "Fyzické čistenie počítačov",
        "Kontrola stavu hardvéru",
        "Výmena termálnych pást",
        "Kontrola ventilátorov",
        "Diagnostika komponentov",
        "Preventívne opatrenia",
      ],
      duration: "1-2 hodiny/počítač",
      deliverables: "Vyčistený počítač + Protokol o stave + Odporúčania",
    },
    {
      id: "eset-products",
      icon: FaShieldAlt,
      title: "ESET Antivírus",
      description:
        "Sme oficiálny ESET dealer s kompletnou ponukou antivírusových riešení pre domácnosti aj firmy",
      features: [
        "ESET Endpoint Security - komplexná ochrana pre firmy",
        "ESET Internet Security - ochrana pre domácnosti a malé firmy",
        "ESET Mobile Security - ochrana mobilných zariadení",
        "ESET Server Security - špecializovaná ochrana serverov",
        "Centralizovaná správa všetkých licencií",
        "Profesionálna inštalácia a konfigurácia",
        "Nepretržitá technická podpora",
        "Školenia používateľov a administrátorov",
        "Pravidelné aktualizácie a monitoring",
        "Zálohovanie a obnova konfigurácie",
      ],
      duration: "Ročné licencie s možnosťou predĺženia",
      deliverables: "Licencie + Implementácia + Školenia + Podpora",
      category: "ESET Partner",
    },
  ];

  const threats = [
    {
      id: "malware",
      icon: FaBug,
      title: "Malware a vírusy",
      severity: "high",
      description:
        "Škodlivý softvér môže poškodiť vaše systémy a ukradnúť citlivé dáta.",
      solution: "Antivírová ochrana, pravidelné aktualizácie a monitoring",
    },
    {
      id: "phishing",
      icon: FaEnvelope,
      title: "Phishing útoky",
      severity: "high",
      description:
        "Podvodné emaily a webstránky na získanie prihlasovacích údajov.",
      solution: "Email security, user training a multi-factor authentication",
    },
    {
      id: "ransomware",
      icon: FaLock,
      title: "Ransomware",
      severity: "critical",
      description: "Šifrovanie dát s požiadavkou výkupného za ich obnovenie.",
      solution: "Zálohovanie dát, endpoint protection a network segmentation",
    },
    {
      id: "ddos",
      icon: FaServer,
      title: "DDoS útoky",
      severity: "medium",
      description: "Preťaženie serverov nadmernou návštevnosťou.",
      solution: "DDoS protection, CDN a load balancing",
    },
    {
      id: "insider",
      icon: FaUsers,
      title: "Insider threats",
      severity: "medium",
      description: "Bezpečnostné riziká od vlastných zamestnancov.",
      solution: "Access controls, monitoring a background checks",
    },
    {
      id: "data-breach",
      icon: FaDatabase,
      title: "Únik dát",
      severity: "critical",
      description: "Neoprávnený prístup k citlivým a osobným údajom.",
      solution: "Data encryption, access controls a incident response",
    },
  ];

  const securityLayers = [
    {
      layer: "Endpoint Protection",
      icon: FaUserShield,
      description: "Ochrana koncových zariadení",
      features: ["Antivírus", "EDR", "Device control", "Patch management"],
    },
    {
      layer: "Network Security",
      icon: FaServer,
      description: "Sieťová bezpečnosť",
      features: ["Firewall", "IPS/IDS", "VPN", "Network segmentation"],
    },
    {
      layer: "Application Security",
      icon: FaShieldAlt,
      description: "Bezpečnosť aplikácií",
      features: ["WAF", "SAST/DAST", "API security", "Code review"],
    },
    {
      layer: "Data Protection",
      icon: FaDatabase,
      description: "Ochrana dát",
      features: ["Encryption", "DLP", "Backup", "Access controls"],
    },
    {
      layer: "Identity Management",
      icon: FaKey,
      description: "Správa identít",
      features: ["MFA", "SSO", "PAM", "Identity governance"],
    },
    {
      layer: "Security Monitoring",
      icon: FaEye,
      description: "Bezpečnostné monitorovanie",
      features: ["SIEM", "SOC", "Threat hunting", "Incident response"],
    },
  ];

  const stats = [
    { number: "99.9%", label: "Úspešnosť detekcie hrozieb" },
    { number: "<1 hodina", label: "Priemerný čas odozvy" },
    { number: "24/7", label: "MONITORING" },
    { number: "500+", label: "Chránených systémov" },
  ];

  const testimonials = [
    {
      name: "Ing. Peter Novák",
      company: "TechCorp s.r.o.",
      position: "IT Manager",
      rating: 5,
      text: "INVER nám pomohol identifikovať kritické zraniteľnosti v našich systémoch. Ich penetračné testovanie bolo veľmi dôkladné a odporúčania praktické.",
    },
    {
      name: "Mária Svobodová",
      company: "Healthcare Plus",
      position: "CISO",
      rating: 5,
      text: "Vďaka ich GDPR auditu sme úspešne prešli kontrolou regulátora. Profesionálny prístup a expertné znalosti na najvyššej úrovni.",
    },
    {
      name: "Mgr. Tomáš Krejčí",
      company: "FinanceSecure Bank",
      position: "Security Officer",
      rating: 5,
      text: "Ich 24/7 SOC služby nám poskytujú potrebnú istotu. Už niekoľkokrát zachránili situáciu vďaka rýchlej detekcii a reakcii.",
    },
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical":
        return "#e53e3e";
      case "high":
        return "#dd6b20";
      case "medium":
        return "#d69e2e";
      case "low":
        return "#38a169";
      default:
        return "#718096";
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical":
        return <FaExclamationTriangle />;
      case "high":
        return <FaExclamationTriangle />;
      case "medium":
        return <FaExclamationTriangle />;
      case "low":
        return <FaCheckCircle />;
      default:
        return <FaCheckCircle />;
    }
  };

  return (
    <div className="security-page">
      {/* Hero Section */}
      <section className="security-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>Počítačová bezpečnosť</h1>
              <p>
                Počítačová bezpečnosť je dnes pojem, ktorý pozná asi každý z
                nás. Zákon o ochrane osobných údajov deklaruje, že v prípade
                pripojenia počítača na sieť Internet, ak tento obsahuje osobné
                dáta, je nutné zabezpečiť ich proti zneužitiu.
              </p>
              <div className="hero-stats">
                {stats.map((stat, index) => (
                  <div key={index} className="hero-stat">
                    <span className="stat-number">{stat.number}</span>
                    <span className="stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
              {/* <div className="hero-cta">
                <button className="btn btn-primary btn-large">
                  <FaShieldAlt />
                  Bezplatná konzultácia
                </button>
                <a href="#services" className="btn btn-outline">
                  Naše služby
                </a>
              </div> */}
            </div>
            <div className="hero-visual">
              <div className="security-shield">
                <FaShieldAlt />
                <div className="shield-layers">
                  <div className="layer layer-1"></div>
                  <div className="layer layer-2"></div>
                  <div className="layer layer-3"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Overview Section */}
      <section className="security-overview">
        <div className="container">
          <div className="security-content">
            <div className="security-text">
              <h2>Prečo je počítačová bezpečnosť dôležitá?</h2>
              <div className="security-description">
                <p>
                  Rizikovosť pripojenia počítača na Internet je už obecne známa.
                  Napadnutie sa môže šíriť po firemnej sieti aj na počítače,
                  ktoré priamo na internet nepristupujú, sú však zapojené do
                  firemnej siete, a to bez pričinenia ľudského prvku.
                </p>
                <p>
                  Každý operačný systém je počítačový program a ako taký
                  obsahuje aj chyby. Keďže každá chyba, ktorá sa dá zneužiť, je
                  spravidla v praxi aj zneužitá, musí tvorca každého programu
                  tento neustále opravovať a vylepšovať jeho zabezpečenie.
                </p>
                <div className="security-warning">
                  <FaExclamationTriangle />
                  <div>
                    <h4>Kritická štatistika</h4>
                    <p>
                      Aktuálna verzia operačného systému Microsoft® Windows®,
                      ktorá sa nainštaluje na počítač, po pripojení na Internet
                      je napadnutá v priemere do <strong>20 minút!</strong>
                    </p>
                  </div>
                </div>
                <p>
                  Bežný používateľ systému sa časom dostane do situácie, kde
                  stráca prehľad a obáva sa, či práve jeho systém nie je ďalším
                  v "poradí". Našťastie, ako existuje nemoc, tak zväčša existuje
                  aj liek.
                </p>
              </div>
            </div>
            <div className="security-visual">
              <div className="attack-timeline">
                <div className="timeline-item">
                  <div className="timeline-icon">
                    <FaWifi />
                  </div>
                  <div className="timeline-content">
                    <h4>Pripojenie na Internet</h4>
                    <p>Počítač sa pripojí na sieť</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-icon">
                    <FaClock />
                  </div>
                  <div className="timeline-content">
                    <h4>20 minút</h4>
                    <p>Priemerný čas do napadnutia</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-icon">
                    <FaBug />
                  </div>
                  <div className="timeline-content">
                    <h4>Napadnutie</h4>
                    <p>Škodlivý softvér infikuje systém</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Threats Section */}
      <section className="threats-section">
        <div className="container">
          <div className="section-header">
            <h2>Kybernetické hrozby</h2>
            <p>
              Poznajte najčastejšie bezpečnostné riziká a ako sa pred nimi
              chrániť
            </p>
          </div>

          <div className="threats-grid">
            {threats.map((threat) => (
              <div
                key={threat.id}
                className={`threat-card ${
                  selectedThreat === threat.id ? "selected" : ""
                }`}
                onClick={() =>
                  setSelectedThreat(
                    selectedThreat === threat.id ? null : threat.id
                  )
                }
              >
                <div className="threat-header">
                  <div className="threat-icon">
                    <threat.icon />
                  </div>
                  <div className="threat-info">
                    <h3>{threat.title}</h3>
                    <div
                      className="severity-badge"
                      style={{
                        backgroundColor: getSeverityColor(threat.severity),
                        color: "white",
                      }}
                    >
                      {getSeverityIcon(threat.severity)}
                      {threat.severity.toUpperCase()}
                    </div>
                  </div>
                </div>

                <p className="threat-description">{threat.description}</p>

                {selectedThreat === threat.id && (
                  <div className="threat-solution">
                    <h4>Riešenie:</h4>
                    <p>{threat.solution}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Layers */}
      <section className="layers-section">
        <div className="container">
          <div className="section-header">
            <h2>Vícevrstvová ochrana</h2>
            <p>Komplexný bezpečnostný model s viacerými úrovňami ochrany</p>
          </div>

          <div className="layers-diagram">
            {securityLayers.map((layer, index) => (
              <div key={index} className="layer-card">
                <div className="layer-icon">
                  <layer.icon />
                </div>
                <div className="layer-content">
                  <h3>{layer.layer}</h3>
                  <p>{layer.description}</p>
                  <div className="layer-features">
                    {layer.features.map((feature, idx) => (
                      <span key={idx} className="feature-tag">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h2>Naše služby v oblasti počítačovej bezpečnosti</h2>
            <p>
              Každý počítač, pripojený do siete Internet alebo do firemnej
              siete, ktorá je pripojená na sieť Internet, potrebuje pravidelné
              prehliadky a očistenie od škodlivých programov. Ponúkame komplexné
              služby:
            </p>
          </div>

          <div className="services-tabs">
            {securityServices.map((service) => (
              <button
                key={service.id}
                className={`service-tab ${
                  activeService === service.id ? "active" : ""
                }`}
                data-category={service.category || ""}
                onClick={() => setActiveService(service.id)}
              >
                <service.icon />
                {service.title}
              </button>
            ))}
          </div>

          {securityServices
            .filter((service) => service.id === activeService)
            .map((service) => (
              <div
                key={service.id}
                className="service-content"
                data-category={service.category || ""}
              >
                <div className="service-info">
                  <div className="service-main">
                    <h3>{service.title}</h3>
                    <p className="service-desc">{service.description}</p>
                    <div className="service-meta">
                      <div className="meta-item">
                        <FaClock />
                        <span>{service.duration}</span>
                      </div>
                      {service.price && service.category !== "ESET Partner" && (
                        <div className="meta-item price">
                          <span>{service.price}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="service-features">
                    <h4>Čo zahŕňa:</h4>
                    <ul>
                      {service.features.map((feature, index) => (
                        <li key={index}>
                          <FaCheckCircle />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="service-deliverables">
                  <h4>Výstupy:</h4>
                  <p>{service.deliverables}</p>
                  <div className="service-cta">
                    <button className="btn btn-primary">
                      <FaPhone />
                      Získať ponuku
                    </button>
                    <Link to="/kontakt" className="btn btn-outline">
                      Kontaktovať nás
                    </Link>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <h2>Náš proces</h2>
            <p>Štandardizovaný postup pre maximálnu efektivitu a kvalitu</p>
          </div>

          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Analýza a plánovanie</h3>
                <p>
                  Dôkladná analýza vašej infraštruktúry a identifikácia rizík
                </p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Implementácia riešení</h3>
                <p>Nasadenie bezpečnostných opatrení a konfigurácia systémov</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Testovanie a validácia</h3>
                <p>
                  Overenie funkčnosti a efektívnosti implementovaných riešení
                </p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Monitoring a údržba</h3>
                <p>Kontinuálne monitorovanie a pravidelné aktualizácie</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2>Referencie klientov</h2>
            <p>Čo hovoria naši klienti o našich bezpečnostných službách</p>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                <p>"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="author-info">
                    <strong>{testimonial.name}</strong>
                    <span>{testimonial.position}</span>
                    <span>{testimonial.company}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Zabezpečte svoju budúcnosť</h2>
            <p>
              Nezanechávajte bezpečnosť náhode. Kontaktujte nás ešte dnes 
              a získajte profesionálnu konzultáciu zdarma.
            </p>
            <div className="cta-features">
              <div className="cta-feature">
                <FaCheckCircle />
                <span>Bezplatná konzultácia</span>
              </div>
              <div className="cta-feature">
                <FaCheckCircle />
                <span>Certifikovaní experti</span>
              </div>
              <div className="cta-feature">
                <FaCheckCircle />
                <span>24/7 podpora</span>
              </div>
            </div>
            <div className="cta-buttons">
              <button className="btn btn-primary btn-large">
                <FaPhone />
                +421 2 / 123 456 789
              </button>
              <Link to="/kontakt" className="btn btn-outline">
                <FaEnvelope />
                Napísať email
              </Link>
            </div>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default SecurityPage;
